import { useId, useRef, useState, type ReactNode } from 'react';
import { Icon } from './icons';
import { Switch } from './controls';
import { DateInput, DateTimeInput, Select, TimeInput } from './pickers';
import { backend, toast } from '../data/store';

/* Small, consistent form fields for the Inspector and settings pages. */

/** "12 / 60": how much of the limit is used, shown beside a field's label (typing stops at the limit). */
const Count = ({ n, max }: { n: number; max: number }) => <small className={`field-count${n >= max ? ' full' : ''}`} aria-live="polite">{n} / {max}</small>;

export function TextField({ label, value, onChange, max = 120, placeholder, type = 'text', hint, counter }: { label: string; value: string; onChange: (v: string) => void; max?: number; placeholder?: string; type?: string; hint?: string; counter?: boolean }) {
  // Dates and times use NoorView's own pickers rather than the browser's.
  if (type === 'date' || type === 'time' || type === 'datetime-local') {
    return (
      <div className="field"><span>{label}</span>
        {type === 'date' ? <DateInput label={label} value={value ?? ''} onChange={onChange} />
          : type === 'time' ? <TimeInput label={label} value={value ?? ''} onChange={onChange} allowClear />
          : <DateTimeInput label={label} value={value ?? ''} onChange={onChange} />}
        {hint && <small className="hint">{hint}</small>}
      </div>
    );
  }
  return (
    <label className="field"><span className={counter ? 'field-head' : undefined}>{label}{counter && <Count n={(value ?? '').length} max={max} />}</span>
      <input className="input" type={type} value={value ?? ''} maxLength={max} placeholder={placeholder} onChange={(e) => onChange(e.target.value.slice(0, max))} />
      {hint && <small className="hint">{hint}</small>}
    </label>
  );
}

export function AreaField({ label, value, onChange, max = 600, rows = 3, dir, counter }: { label: string; value: string; onChange: (v: string) => void; max?: number; rows?: number; dir?: 'rtl'; counter?: boolean }) {
  return (
    <label className="field"><span className={counter ? 'field-head' : undefined}>{label}{counter && <Count n={(value ?? '').length} max={max} />}</span>
      <textarea className="input" rows={rows} value={value ?? ''} maxLength={max} dir={dir} onChange={(e) => onChange(e.target.value.slice(0, max))} />
    </label>
  );
}

export function NumberField({ label, value, onChange, min, max, step = 1, suffix }: { label: string; value: number; onChange: (v: number) => void; min?: number; max?: number; step?: number; suffix?: string }) {
  const [text, setText] = useState<string | null>(null);
  return (
    <label className="field"><span>{label}</span>
      <span className="input-wrap">
        <input className="input mono" inputMode="decimal" value={text ?? String(value ?? 0)}
          onChange={(e) => { setText(e.target.value); const n = Number(e.target.value.replace(/[^\d.-]/g, '')); if (e.target.value.trim() !== '' && Number.isFinite(n)) onChange(clamp(n, min, max)); }}
          onBlur={() => setText(null)} onKeyDown={(e) => { if (e.key === 'ArrowUp') { e.preventDefault(); onChange(clamp((value ?? 0) + step, min, max)); } if (e.key === 'ArrowDown') { e.preventDefault(); onChange(clamp((value ?? 0) - step, min, max)); } }} />
        {suffix && <em>{suffix}</em>}
      </span>
    </label>
  );
}
const clamp = (n: number, min?: number, max?: number) => Math.max(min ?? -Infinity, Math.min(max ?? Infinity, n));

export function SelectField<T extends string>({ label, value, onChange, options }: { label: string; value: T; onChange: (v: T) => void; options: Array<{ value: T; label: string }> }) {
  return (
    <div className="field"><span>{label}</span>
      <Select label={label} value={value} onChange={onChange} options={options} />
    </div>
  );
}

export function ToggleRow({ label, checked, onChange, hint }: { label: string; checked: boolean; onChange: (v: boolean) => void; hint?: string }) {
  return (
    <div className="toggle-row">
      <div><span className="label">{label}</span>{hint && <small className="hint">{hint}</small>}</div>
      <Switch label={label} checked={!!checked} onChange={onChange} />
    </div>
  );
}

/** Editable list with add, remove and reorder (used for announcements, events, ticker lines…). */
export function ListEditor<T extends { id: string }>({ label, items, onChange, make, render, max = 30, addLabel = 'Add', title }: {
  label: string; items: T[]; onChange: (items: T[]) => void; make: () => T; render: (item: T, set: (patch: Partial<T>) => void) => ReactNode; max?: number; addLabel?: string; title: (item: T) => string;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const set = (id: string, patch: Partial<T>) => onChange(items.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  const move = (i: number, d: number) => { const a = [...items]; const [x] = a.splice(i, 1); a.splice(i + d, 0, x!); onChange(a); };
  return (
    <div className="list-ed">
      <div className="row"><span className="section-label">{label}</span><span className="spacer" /><span className="hint">{items.length}/{max}</span></div>
      {items.map((it, i) => (
        <div key={it.id} className={`li ctl${open === it.id ? ' open' : ''}`}>
          <div className="li-head">
            <button type="button" className="li-title" onClick={() => setOpen(open === it.id ? null : it.id)} aria-expanded={open === it.id}>
              <Icon name={open === it.id ? 'chevronDown' : 'chevron'} size={16} /><span>{title(it) || 'Untitled'}</span>
            </button>
            <button type="button" className="lbtn" aria-label="Move up" disabled={i === 0} onClick={() => move(i, -1)}><Icon name="front" size={16} /></button>
            <button type="button" className="lbtn" aria-label="Move down" disabled={i === items.length - 1} onClick={() => move(i, 1)}><Icon name="backward" size={16} /></button>
            <button type="button" className="lbtn danger" aria-label="Remove" onClick={() => onChange(items.filter((x) => x.id !== it.id))}><Icon name="trash" size={16} /></button>
          </div>
          {open === it.id && <div className="li-body">{render(it, (p) => set(it.id, p))}</div>}
        </div>
      ))}
      <button type="button" className="btn sm plain" disabled={items.length >= max} onClick={() => { const n = make(); onChange([...items, n]); setOpen(n.id); }}><Icon name="plus" size={16} />{addLabel}</button>
    </div>
  );
}

/* ---------- images ---------- */

export { prepareImage } from './image';
import { prepareImage } from './image';

export function UploadButton({ label = 'Upload image', onUploaded, multiple }: { label?: string; onUploaded: (urls: string[]) => void; multiple?: boolean }) {
  const ref = useRef<HTMLInputElement>(null);
  const id = useId();
  const [busy, setBusy] = useState(false);
  return (
    <>
      <input ref={ref} id={id} type="file" accept="image/png,image/jpeg,image/webp,image/gif" multiple={multiple} hidden
        onChange={async (e) => {
          const files = [...(e.target.files ?? [])];
          e.target.value = '';
          if (!files.length) return;
          setBusy(true);
          try {
            const urls: string[] = [];
            for (const f of files.slice(0, 20)) urls.push(await backend.uploadMedia(await prepareImage(f), f.name));
            onUploaded(urls);
          } catch (x) { toast(x instanceof Error ? x.message : 'Upload failed.', { bad: true }); }
          finally { setBusy(false); }
        }} />
      <button type="button" className="btn sm plain" disabled={busy} onClick={() => ref.current?.click()}><Icon name="upload" size={16} />{busy ? 'Uploading…' : label}</button>
    </>
  );
}
